import { useCallback, useEffect, useRef, useState } from 'react';
import { GroupedTanStackVirtualThumbnailsList } from './GroupedTanStackVirtualThumbnailsList';
import {
  makeSelectBundlePageIndexByPageId,
  makeSelectBundlePageIndexMap,
  selectBundlePagesData,
  selectBundleSections,
  selectMedicalMatchTabActive,
  selectPageInfoCache,
  selectSharedDataActiveBundleType,
  selectSharedDataBundleId,
  selectSharedDataInitialPageId,
  setBundleSelectedThumbnailIndexes,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { extractGroupSectionLabelsAndPageData } from './utils';
import { GridGroup, GroupSectionLabel, HandleDropRequestProps, IsDropRestrictedProps } from './types';
import { useEditClusterDate, useViewportPageIndexContext } from '../../hooks';
import { ThumbnailItemClickProps } from './components';
import { AccessControlAction } from '@/constants';
import { useAccessControl } from '@/hooks';
import { ContextMenu, useGlobalToast } from '@repo/atomic-components';
import { useContextMenu } from './hooks';
import { isEndpointLockError, useReorderBundlePagesMutation } from '@/api';
import { MoveSectionsDialog } from '../MoveSectionsDialog';
import { RetrievePagesDialog } from '../retrievePagesDialog';
import { EditClusterDateDialog } from '../EditClusterDateDialog';

export const GridContainer = () => {
  const dispatch = useAppDispatch();

  const documentReviewPageContext = useViewportPageIndexContext();

  const [groups, setGroups] = useState<GridGroup[]>([]);
  const [selectedThumbnailPageIds, setSelectedThumbnailPageIds] = useState<string[]>([]);

  const isDraggingRef = useRef(false);
  // Store updates that land mid-drag would wipe the drag preview, so they are
  // parked here and applied once the drag is over.
  const groupsPendingDragEndRef = useRef<GridGroup[] | null>(null);
  const hasRequestedReorderRef = useRef(false);

  const bundleId = useAppSelector(selectSharedDataBundleId);
  const bundleSections = useAppSelector(selectBundleSections);
  const sharedDataInitialPageId = useAppSelector(selectSharedDataInitialPageId);
  const activeBundleType = useAppSelector(selectSharedDataActiveBundleType);
  const isMedicalMatchTabActive = useAppSelector(selectMedicalMatchTabActive);

  const pageIndexMap = useAppSelector(makeSelectBundlePageIndexMap);
  const pageDataMap = useAppSelector(selectBundlePagesData);
  const pageInfoCache = useAppSelector(selectPageInfoCache);
  const getBundlePageIndexByPageId = useAppSelector(makeSelectBundlePageIndexByPageId);

  const isEditBundles = useAccessControl(AccessControlAction.EDIT_BUNDLES);

  const globalToastRef = useGlobalToast();
  const [triggerReorderBundlePages] = useReorderBundlePagesMutation();

  const {
    editState: editClusterDateState,
    openForClusters: openEditClusterDate,
    close: closeEditClusterDate,
    confirm: confirmEditClusterDate,
  } = useEditClusterDate();

  // The label's own text is the cluster's current date, so it doubles as the
  // seed for the picker.
  const handleEditClusterDate = useCallback(
    (label: GroupSectionLabel) => {
      openEditClusterDate([label.id], label.text);
    },
    [openEditClusterDate],
  );

  const {
    contextMenuRef,
    contextMenuModel,
    handleContextMenuShow,
    isMoveSectionsDialogVisible,
    setIsMoveSectionsDialogVisible,
    isRetrievePagesDialogVisible,
    setIsRetrievePagesDialogVisible,
    bundleSelectedThumbnailIndexesRef,
    bundleCutOutThumbnailIndexes,
  } = useContextMenu({
    activeBundleType: activeBundleType,
    bundleId: bundleId,
    bundleSections: bundleSections,
  });

  useEffect(() => {
    const groupsWithLabels = extractGroupSectionLabelsAndPageData(
      bundleSections,
      pageIndexMap,
      pageDataMap,
      pageInfoCache,
    );

    if (isDraggingRef.current) {
      groupsPendingDragEndRef.current = groupsWithLabels;
      return;
    }

    setGroups(groupsWithLabels);
  }, [bundleSections, pageDataMap, pageIndexMap, pageInfoCache]);

  const handleDragStateChange = useCallback((isDragging: boolean) => {
    isDraggingRef.current = isDragging;

    if (isDragging) {
      hasRequestedReorderRef.current = false;
      groupsPendingDragEndRef.current = null;
      return;
    }

    // A successful reorder brings its own refreshed sections back, so anything
    // parked during the drag is already out of date. It stays parked until the
    // request settles though, because a failed reorder rolls back to a snapshot
    // that predates the parked update.
    if (groupsPendingDragEndRef.current && !hasRequestedReorderRef.current) {
      setGroups(groupsPendingDragEndRef.current);
      groupsPendingDragEndRef.current = null;
    }
  }, []);

  const handleThumbnailItemClick = useCallback(
    ({ pageIndex }: ThumbnailItemClickProps) => {
      documentReviewPageContext.onScrollToPageIndexInReactVirtualizedViewport(pageIndex);
    },
    [documentReviewPageContext],
  );

  const handleSelectedThumbnailPageIds = useCallback(
    (selectedIds: string[]) => {
      setSelectedThumbnailPageIds(selectedIds);
      bundleSelectedThumbnailIndexesRef.current = groups
        .flatMap((group) => group.items)
        .filter((item) => selectedIds.includes(item.id))
        .map((item) => item.pageIndex);
      if (activeBundleType) {
        dispatch(
          setBundleSelectedThumbnailIndexes({
            key: activeBundleType,
            data: selectedIds.map((item) => getBundlePageIndexByPageId(item)),
          }),
        );
      }
    },
    [activeBundleType, bundleSelectedThumbnailIndexesRef, dispatch, getBundlePageIndexByPageId, groups],
  );

  const isDropRestricted = useCallback(
    ({ activeGroup, group }: IsDropRestrictedProps) => {
      const isDifferentProviders =
        !!activeGroup.groupProviderId &&
        !!group.groupProviderId &&
        activeGroup.groupProviderId !== group.groupProviderId;

      // If not different providers, allow the drop
      if (!isDifferentProviders) return false;

      // Check if moving between original and additional record providers
      const sourceProvider = bundleSections.find((section) => section.id === activeGroup.groupProviderId);
      const targetProvider = bundleSections.find((section) => section.id === group.groupProviderId);
      // IF either provider not found, restrict the drop
      if (!sourceProvider || !targetProvider) return true;

      // Check if one has date (additional records) and the other doesn't (original)
      const sourceHasDate = !!sourceProvider.date;
      const targetHasDate = !!targetProvider.date;

      // If mixing original (no date) and additional (has date) providers, restrict the drop
      const isMixedMove = (sourceHasDate && !targetHasDate) || (!sourceHasDate && targetHasDate);

      return isDifferentProviders || isMixedMove;
    },
    [bundleSections],
  );

  const handleDropRequest = useCallback(
    ({ targetParentId, anchorPageId, targetIndex, pageIds, pasteLocation, rollbackGroups }: HandleDropRequestProps) => {
      if (bundleId && targetParentId && anchorPageId && pageIds.length) {
        hasRequestedReorderRef.current = true;
        triggerReorderBundlePages({
          bundleId: bundleId,
          targetParentId: targetParentId,
          anchorPageId: anchorPageId,
          targetIndex: targetIndex,
          pageIds: pageIds,
          pasteLocation: pasteLocation,
        })
          .unwrap()
          .catch((error) => {
            // A lock refusal already has its own toast, but the drop is still rolled back
            if (!isEndpointLockError(error) && globalToastRef.current) {
              globalToastRef.current.show({
                severity: 'warn',
                summary: 'Moving items',
                detail: 'Pages cannot be moved',
                sticky: false,
                life: 5000,
              });
            }
            setGroups(groupsPendingDragEndRef.current ?? rollbackGroups);
            groupsPendingDragEndRef.current = null;
          });
      }
    },
    [bundleId, globalToastRef, triggerReorderBundlePages],
  );

  return (
    <div className="bg-fletchers-white m-0 flex h-full w-full flex-col p-0 pt-[12px]">
      {isMoveSectionsDialogVisible && (
        <MoveSectionsDialog pageIds={selectedThumbnailPageIds} setVisible={setIsMoveSectionsDialogVisible} />
      )}

      {isRetrievePagesDialogVisible && <RetrievePagesDialog setVisible={setIsRetrievePagesDialogVisible} />}

      {isEditBundles && <ContextMenu ref={contextMenuRef} model={contextMenuModel} />}

      {editClusterDateState && (
        <EditClusterDateDialog
          key={editClusterDateState.openId}
          initialDate={editClusterDateState.initialDate}
          onCancel={closeEditClusterDate}
          onConfirm={confirmEditClusterDate}
        />
      )}

      <GroupedTanStackVirtualThumbnailsList
        ref={documentReviewPageContext.groupedTanStackVirtualThumbnailsListRef}
        groups={groups}
        setGroups={setGroups}
        activeBundleType={activeBundleType}
        isDragAndDropDisabled={isMedicalMatchTabActive}
        selectedThumbnailPageIds={selectedThumbnailPageIds}
        bundleCutOutThumbnailIndexes={bundleCutOutThumbnailIndexes}
        onSelectionChange={handleSelectedThumbnailPageIds}
        onThumbnailItemClick={handleThumbnailItemClick}
        onContextMenuShow={handleContextMenuShow}
        onDropRequest={handleDropRequest}
        isDropRestricted={isDropRestricted}
        onDragStateChange={handleDragStateChange}
        onEditClusterDate={isEditBundles ? handleEditClusterDate : undefined}
        initialScrollToPageId={sharedDataInitialPageId}
      />
    </div>
  );
};
